import { FC, useCallback } from "react";
import { isEmpty } from "lodash";
import { Alert, Box, Divider, Stack, Typography } from "@mui/material";
import __ from "src/utils/Translations";
import getImportErrorMessage, {
  ErrorCode,
} from "src/utils/Company/Absences/getImportErrorMessage";
import {
  excludeMainErrorsFromRowErrors,
  getDuplicateRelation,
  getSortedRowErrorEntries,
  hasExceededImportErrorLimit,
  normalizeImportErrors,
} from "src/utils/Company/Absences/importErrors";
import {
  ErrorType,
  IImportErrorDetails,
  IImportErrors,
  IImportRowError,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

interface ImportErrorListProps {
  importErrors: IImportErrors;
}

const DUPLICATE_ITEM_TEST_ID = "absences-import-error-list__duplicate-item";
const ERROR_ITEM_TEST_ID = "absences-import-error-list__item";
const MAIN_ERROR_ITEM_TEST_ID = "absences-import-error-list__main-item";

const getDuplicateRelationInfo = (
  errorDetails: IImportErrorDetails | null | undefined
): string | undefined => {
  const { rows, hiddenCount } = getDuplicateRelation(errorDetails);

  if (isEmpty(rows)) {
    return undefined;
  }

  if (hiddenCount > 0) {
    return __("duplikat wierszy: {0} i {1} więcej", [
      rows.join(", "),
      String(hiddenCount),
    ]);
  }

  if (rows.length === 1) {
    return __("duplikat wiersza: {0}", [rows[0]]);
  }

  return __("duplikat wierszy: {0}", [rows.join(", ")]);
};

const ImportErrorList: FC<ImportErrorListProps> = ({ importErrors }) => {
  const renderErrorItem = useCallback(
    (
      error: IImportRowError,
      row: string,
      key: string,
      columnInfo?: string,
      relationInfo?: string,
      testId: string = ERROR_ITEM_TEST_ID
    ) => (
      <div key={key} data-testid="absences-import-error-list">
        <Stack
          direction="row"
          sx={{ alignItems: "center" }}
          spacing={4}
          data-testid={testId}
        >
          <Box sx={{ flexBasis: "70%" }}>
            <Typography variant="body2">
              {getImportErrorMessage(error.errorCode)}
            </Typography>
          </Box>
          <Box sx={{ flexBasis: "30%" }}>
            <Typography variant="body2">
              {__("Wiersz")}: <strong>{row}</strong>
              {columnInfo && (
                <>
                  , {__("kolumna")}: <strong>{columnInfo}</strong>
                </>
              )}
              {relationInfo && <>, {relationInfo}</>}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ m: "0.5rem 0" }} />
      </div>
    ),
    []
  );

  const normalizedErrors = normalizeImportErrors(importErrors.errors);
  const mainErrors = importErrors.mainErrors ?? [];
  const rowErrorEntries = getSortedRowErrorEntries(
    excludeMainErrorsFromRowErrors(importErrors.rowErrors ?? {}, mainErrors)
  );
  const hasExceededErrorLimit = hasExceededImportErrorLimit(
    importErrors.rowsWithErrorsCount,
    importErrors.rowsWithErrorsLimit
  );

  return (
    <>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{ mb: "1rem" }}
        data-testid="absences-import-error-list__failed"
      >
        {__("Plik odrzucony. Błędy napotkane podczas importu:")}
      </Typography>
      {normalizedErrors.includes(ErrorType.UNPARSEABLE_FILE) && (
        <Typography variant="body2" color="error">
          {__("Import niemożliwy, nieznany błąd.")}
        </Typography>
      )}
      {normalizedErrors.includes(ErrorType.INVALID_COLUMN_LAYOUT) && (
        <Alert
          severity="error"
          data-testid="absences-import-error-list__invalid-column-layout"
        >
          {__(
            "Nieprawidłowy układ pliku importu, sprawdź zgodność kolumn z szablonem."
          )}
        </Alert>
      )}
      {mainErrors.map(({ error, rowsCount }) => {
        const columnInfo = error.columns
          ?.map((column) => column.name)
          .join(", ");

        // Main errors are unique per error code and columns
        return (
          <div
            key={`main-error-${error.errorCode}-${columnInfo ?? ""}`}
            data-testid="absences-import-error-list"
          >
            <Stack
              direction="row"
              sx={{ alignItems: "center" }}
              spacing={4}
              data-testid={MAIN_ERROR_ITEM_TEST_ID}
            >
              <Box sx={{ flexBasis: "70%" }}>
                <Typography variant="body2">
                  {getImportErrorMessage(error.errorCode)}
                </Typography>
              </Box>
              <Box sx={{ flexBasis: "30%" }}>
                <Typography variant="body2">
                  {__("Liczba wierszy z błędem")}: <strong>{rowsCount}</strong>
                  {columnInfo && (
                    <>
                      , {__("kolumna")}: <strong>{columnInfo}</strong>
                    </>
                  )}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ m: "0.5rem 0" }} />
          </div>
        );
      })}
      {hasExceededErrorLimit && (
        <Alert
          severity="warning"
          sx={{ mb: "1rem" }}
          data-testid="absences-import-error-list__limit"
        >
          {__(
            "Liczba błędów przekroczyła maksymalny limit wyświetlania wynoszący {0} pozycji. Popraw dane i ponownie wykonaj import.",
            [String(importErrors.rowsWithErrorsLimit)]
          )}
        </Alert>
      )}
      {rowErrorEntries.flatMap(([row, errors]) =>
        errors.flatMap((error, errorIndex) => {
          if (
            error.errorCode === ErrorCode.ABSENCE_REQUEST_DUPLICATED_IN_FILE
          ) {
            return renderErrorItem(
              error,
              row,
              `${row}-${errorIndex}`,
              undefined,
              getDuplicateRelationInfo(error.errorDetails),
              DUPLICATE_ITEM_TEST_ID
            );
          }

          if (error.errorCode === ErrorCode.EMPLOYEE_PERSONAL_DATA_MISMATCH) {
            const customColumnInfo =
              error.columns?.map((column) => column.name).join(", ") ||
              "brak danych";

            return renderErrorItem(
              error,
              row,
              `${row}-${errorIndex}`,
              customColumnInfo
            );
          }

          const columns = error.columns ?? [];

          if (isEmpty(columns)) {
            return renderErrorItem(error, row, `${row}-${errorIndex}`);
          }

          return columns.map((column, columnIndex) =>
            renderErrorItem(
              error,
              row,
              `${row}-${errorIndex}-${columnIndex}`,
              column.name
            )
          );
        })
      )}
    </>
  );
};

export default ImportErrorList;
